import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't show on admin or mobile pages
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/mobile')) {
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing the banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const acceptAll = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    // Reload to start tracking
    window.location.reload();
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookie_consent', 'necessary_only');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const declineAll = () => {
    localStorage.setItem('cookie_consent', 'declined');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-[9998] backdrop-blur-sm" />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-slate-900 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500/20 p-2 rounded-lg">
                <Cookie className="text-orange-400" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Cookie Settings</h3>
                <p className="text-sm text-slate-400">We value your privacy</p>
              </div>
            </div>
            <button
              onClick={declineAll}
              className="text-slate-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6">
            <p className="text-slate-300 mb-4">
              We use cookies and similar technologies to enhance your browsing experience, 
              analyze site traffic, and understand where our visitors come from. This helps 
              us improve our services and provide you with relevant content.
            </p>

            {/* Expandable Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-orange-400 hover:text-orange-300 text-sm flex items-center mb-4"
            >
              <Shield size={16} className="mr-1" />
              {showDetails ? 'Hide details' : 'Learn more about the cookies we use'}
            </button>

            {showDetails && (
              <div className="bg-slate-800/50 rounded-lg p-4 mb-4 space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold text-white flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                    Essential Cookies
                  </h4>
                  <p className="text-slate-400 ml-5">
                    Required for the website to function. These cannot be disabled.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white flex items-center">
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                    Analytics Cookies
                  </h4>
                  <p className="text-slate-400 ml-5">
                    Help us understand how visitors interact with our website by collecting 
                    information such as pages visited, time spent, browser type, device type, 
                    and approximate location based on IP address.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white flex items-center">
                    <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
                    Marketing Cookies
                  </h4>
                  <p className="text-slate-400 ml-5">
                    Track visitors across websites to display relevant advertisements and 
                    measure the effectiveness of our marketing campaigns.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptAll}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                data-testid="cookie-accept-all"
              >
                Accept All Cookies
              </button>
              <button
                onClick={acceptNecessary}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                data-testid="cookie-accept-necessary"
              >
                Necessary Only
              </button>
              <button
                onClick={declineAll}
                className="flex-1 border border-slate-600 hover:border-slate-500 text-slate-300 font-semibold py-3 px-6 rounded-lg transition-colors"
                data-testid="cookie-decline"
              >
                Decline All
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-4 text-center">
              By clicking "Accept All", you consent to our use of cookies. 
              You can change your preferences at any time in our Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
